import { CommonModule } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { TranslateModule } from "@ngx-translate/core";

import { ActionButtonComponent } from "../ui-components/buttons/action-button/action-button.component";
import {
  ActionButtonSize,
  ActionButtonType,
} from "../ui-components/buttons/action-button/action-button.model";
import { IPayment } from "src/app/services/model/get-trip-data.model";
import { SharedModule } from "src/app/shared.module";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { SharedApiService } from "src/app/services/api/sharedApi.service";
import { NgxSpinnerService } from "ngx-spinner";
import { LoginService } from "src/app/services/login.service";
import { StateService } from "src/app/services/state.service";
import { DbSchemeEnum } from "src/app/enums/db-scheme.enum";
import { ProcedureNameEnum } from "src/app/enums/procedure-name.enum";
import { IExecuteProcedureRequest, IExecuteProcedureResult, IGetCashMovementsSummaryRequest } from "src/app/services/model/pos.api.model";
import { from, lastValueFrom, catchError, finalize, of, tap, Observable } from "rxjs";

export type ReturnPaymentDialogData = {
  payment: IPayment;
};

export type ReturnPaymentDialogResult = {
  confirmed: boolean;
  note: string;
};

@Component({
  selector: "app-return-payment-dialog",
  standalone: true,
  imports: [CommonModule, MatDialogModule, TranslateModule, SharedModule, ActionButtonComponent, ReactiveFormsModule],
  templateUrl: "./return-payment-dialog.component.html",
  styleUrls: ["./return-payment-dialog.component.scss"],
})
export class ReturnPaymentDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<ReturnPaymentDialogComponent>);
  public readonly data = inject<ReturnPaymentDialogData>(MAT_DIALOG_DATA);
  private readonly _sharedApiService = inject(SharedApiService);
  private readonly _spinner = inject(NgxSpinnerService);
  private readonly _loginService = inject(LoginService);
  public readonly stateService = inject(StateService);

  ActionButtonSize = ActionButtonSize;
  ActionButtonType = ActionButtonType;

  availableCash: number | null = null;
  cashInsufficient: boolean = false;

  form = new FormGroup({
    note: new FormControl<string>("", Validators.required),
  });

  async ngOnInit(): Promise<void> {
    if (this.data.payment?.PaymentType === TenderType.Cash) {
      const payment = this.data.payment;
      
      await this.loadAvailableCash(payment);
    }
  }

  private async loadAvailableCash(payment: IPayment): Promise<void> {
    this._spinner.show();

    const jsonData = {
        CurrencyCode: payment.Currency,
        ReturnToResult: 0,
        PaymentName: payment.PaymentName,
    } as IGetCashMovementsSummaryRequest;

    const request: IExecuteProcedureRequest<IGetCashMovementsSummaryRequest> = {
      DbScheme: DbSchemeEnum.Dert,
      ProcedureName: ProcedureNameEnum.GetCashMovementsSummary,
      Parameters: jsonData,
    };
    
    // Create observable and apply operators
    const executeRequest$ = this._sharedApiService.executeProcedureObservable(request);
    const withProcessing$ = executeRequest$.pipe(
      tap(result => this.updateCashData(result)),
      catchError(error => {
        console.error('Failed to load cash movements summary:', error);
        return of(null);
      }),
      finalize(() => this._spinner.hide())
    );

    // Execute and await completion
    await lastValueFrom(withProcessing$);
  }

  private updateCashData(result: any): void {
    const totalMovement = result?.data?.TotalMovement;
    const isValid = totalMovement !== undefined && totalMovement !== null;

    if (isValid) {
      this.availableCash = totalMovement;
      this.cashInsufficient = Math.abs(this.data.payment.Amount) > this.availableCash;
    }
  }

  cancel(): void {
    this.dialogRef.close({ confirmed: false, note: "" } as ReturnPaymentDialogResult);
  }

  confirm(): void {
    if (this.form.invalid || this.cashInsufficient) {
      return;
    }
    const note = this.form.get("note")?.value?.trim() ?? "";
    this.dialogRef.close({ confirmed: true, note } as ReturnPaymentDialogResult);
  }
}
